import { Search, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

interface Cours {
  id: number;
  titre: string;
  image: string;
  bgClass: string;
  description: string;
  nombreEtudiants: number;
}

const ProfesseurDashboard = () => {
  // Données JSON des cours
  const cours: Cours[] = [
    {
      id: 1,
      titre: "Fondements et valeurs de la République",
      image: "/images/valeur.png",
      bgClass: "bg-gradient-to-r from-pink-100 to-purple-100",
      description: "Formation sur les valeurs fondamentales de la République",
      nombreEtudiants: 45,
    },
    {
      id: 2,
      titre: "Éthique et déontologie du Fonctionnaire",
      image: "/images/Ethique-et-Deontologie.jpg",
      bgClass: "bg-gray-300",
      description:
        "Principes éthiques et déontologiques pour les fonctionnaires",
      nombreEtudiants: 32,
    },
  ];

  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState("");
  // État pour les onglets
  const [activeTab, setActiveTab] = useState("dashboard");

  // Filtrer les cours en fonction de la recherche
  const filteredCours = searchQuery
    ? cours.filter((cours) =>
        cours.titre.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : cours;

  return (
    <div className="bg-[#fefaf6]">
      <div className="container mx-auto px-4 py-4 mobile:px-3 tablet:px-4">
        {/* En-tête de bienvenue */}
        <div className="mb-6 mobile:mb-4 tablet:mb-5">
          <h1 className="text-2xl font-bold text-gray-800 mobile:text-xl tablet:text-xl desktop:text-3xl">
            Bienvenue Prof. KOUASSI
          </h1>
          <p className="text-sm text-gray-600 mobile:text-xs tablet:text-sm desktop:text-base">
            Gérez vos cours et suivez la progression de vos étudiants
          </p>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mobile:gap-3 tablet:gap-4 mobile:mb-6 tablet:mb-6">
          <div className="border border-green-300 rounded-md p-4 mobile:p-3 tablet:p-3 bg-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-semibold mobile:text-sm tablet:text-base">
                  Vue d'ensemble des cours
                </h2>
                <p className="text-xs text-gray-500 mobile:text-[10px] tablet:text-xs">
                  Mis à jour aujourd'hui
                </p>
              </div>
              <div className="bg-orange-50 text-orange-500 rounded-full p-2 flex items-center justify-center h-10 w-10 mobile:h-8 mobile:w-8 tablet:h-9 tablet:w-9">
                <span className="font-bold mobile:text-sm tablet:text-base">
                  {cours.length}
                </span>
              </div>
            </div>
          </div>

          <Link
            to="/professeur/live"
            className="border border-orange-300 rounded-md p-4 mobile:p-3 tablet:p-3 bg-white hover:bg-orange-50 transition-colors duration-200"
          >
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-semibold mobile:text-sm tablet:text-base text-orange-600">
                  Mode Direct
                </h2>
                <p className="text-xs text-gray-500 mobile:text-[10px] tablet:text-xs">
                  Suivez vos étudiants en temps réel
                </p>
              </div>
              <div className="bg-orange-100 text-orange-500 rounded-full p-2 flex items-center justify-center h-10 w-10 mobile:h-8 mobile:w-8 tablet:h-9 tablet:w-9">
                <span className="font-bold mobile:text-sm tablet:text-base">
                  Live
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* Barre de recherche */}
        <div className="relative mb-8 mobile:mb-5 tablet:mb-6">
          <input
            type="text"
            placeholder="Rechercher un cours..."
            className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 mobile:top-2 tablet:top-2" />
        </div>

        {/* Onglets */}
        <div className="flex border-b border-gray-200 mb-8 mobile:mb-6 tablet:mb-6 mobile:overflow-x-auto tablet:overflow-x-auto">
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "dashboard"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("dashboard")}
          >
            TABLEAU DE BORD
          </button>
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "cours"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("cours")}
          >
            MES COURS
          </button>
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "etudiants"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("etudiants")}
          >
            ÉTUDIANTS
          </button>
        </div>

        {/* Liste des cours */}
        {activeTab === "dashboard" && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 mobile:text-xl mobile:mb-4 tablet:text-xl tablet:mb-5 desktop:text-2xl">
              Mes cours
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile:gap-4 tablet:gap-5 desktop:gap-8">
              {filteredCours.map((cours) => (
                <div
                  key={cours.id}
                  className="bg-white rounded-md shadow-sm overflow-hidden mobile:shadow tablet:shadow"
                >
                  <div
                    className={`h-56 ${cours.bgClass} relative mobile:h-48 tablet:h-52`}
                  >
                    <img
                      src={cours.image}
                      alt={cours.titre}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-2">
                      {cours.titre}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {cours.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        {cours.nombreEtudiants} étudiants
                      </span>
                      <Link
                        to={`/cours/${cours.id}`}
                        className="flex items-center text-orange-500 hover:text-orange-600"
                      >
                        <span className="text-sm">Voir détails</span>
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfesseurDashboard;
